import { mobileMapViewV2AssignmentSelector } from '@redux/experiments/selectors/mobile-map-view-v2';
import type { State } from '@typings/redux';
import type { Raw, Similar } from 'types/vendor';
import type {
	Impression,
	ImpressionType,
	SegmentImpression,
} from 'types/vendorImpressions';
import NavigationHelper from '../../../helpers/navigation';

const getMarketCode = (state: Redux.State): string | null => {
	switch (state.page.pageType) {
		case 'best of weddings': {
			return state.bow.location.market || null;
		}

		case 'category': {
			return state.location.marketCode || null;
		}

		case 'city': {
			return state.settings.marketCode || null;
		}

		case 'storefront': {
			const vendorMarketCode = state.vendor.vendorRaw?.marketCode;
			const referredMarketCode =
				NavigationHelper.sanitizedVendorReferredMarketCode(vendorMarketCode);
			return referredMarketCode || vendorMarketCode;
		}

		default:
			return null;
	}
};

const getStateCode = (state: Redux.State): string | null => {
	if (state.page.pageType === 'state') {
		return state.states.code;
	}

	return null;
};
export const isOnDirectoryPage = (state: Redux.State): boolean =>
	state.page?.pageType === 'city';

const isOnLandingPages = (state: Redux.State): boolean =>
	['category', 'state'].includes(state.page?.pageType);

const isSortScoreEnabled = (state: Redux.State): boolean =>
	isOnDirectoryPage(state) || isOnLandingPages(state);

export const getSortScoreId = <T extends Vendor.PopulatedBase>(
	state: Redux.State,
	vendor: T,
): number | null => {
	if (isSortScoreEnabled(state)) {
		return vendor.personalizedScoreId || null;
	}

	return null;
};

const getListingDetailsImpressionType = (
	state: Redux.State,
): VendorImpressions.ListingDetailsImpressionType => {
	if (isOnDirectoryPage(state)) {
		return 'IVOL';
	}

	return null;
};

const getDirectoryStyle = (
	state: Redux.State,
): VendorImpressions.DirectoryStyle => {
	const isMapView = mobileMapViewV2AssignmentSelector(state) === 'map-toolbar';
	return isMapView ? 'image' : 'list';
};

export const getListingDetails = (
	state: Redux.State,
	cardUiLocation?: string,
): string => {
	const onDirectoryPage = isOnDirectoryPage(state);
	const listingDetailsImpressionType = getListingDetailsImpressionType(state);
	const page = onDirectoryPage ? state.search.pagination.page : null;
	const directoryStyle = getDirectoryStyle(state);
	const listingDetails: VendorImpressions.ListingDetails = {
		category_code: state.category.code,
		market_code: getMarketCode(state),
		page,
		impression_type: listingDetailsImpressionType,
		impression_style: directoryStyle,
		state_code: getStateCode(state),
		...(onDirectoryPage && {}),
	};

	return JSON.stringify(listingDetails);
};

const hasLeadId = (impressions: VendorImpressions.Impression[]): boolean => {
	return impressions.some((impression) => impression.list.match(/Lead Gen:/));
};

const getLeadId = (state: Redux.State): string | null =>
	state.rfq.rfqFormData.conversationId || null;

const hasParentVendorId = (
	impressions: VendorImpressions.Impression[],
): boolean => {
	return impressions.some((impression) =>
		impression.list.match(/Lead Gen:|Storefront -/),
	);
};

const getParentVendorId = (state: Redux.State): string | null =>
	state.rfq.storefrontId || state.vendor.vendorRaw?.id || null;

/** @internal */
export const getFirstImageId = (impression: Impression) => {
	const { ai_photo_id, storefront_card_id } = impression;
	if (ai_photo_id) {
		return ai_photo_id;
	}

	return storefront_card_id || null;
};

const formatSegmentVendorImpression = (
	impression: Impression,
	state: State,
) => {
	return {
		storefront_id: impression.id,
		parent_lead_id: hasLeadId([impression]) ? getLeadId(state) : null,
		parent_lead_vendor_id: hasParentVendorId([impression])
			? getParentVendorId(state)
			: null,
		product_tier: impression.variant,
		list_position: impression.position,
		sort_score_id: impression.directory_sort_score_id,
		first_image: getFirstImageId(impression),
		storefront_completion_status:
			impression.storefront_completion_status || null,
		impression_timestamp: impression.impression_timestamp,
	};
};

export const getBucketedImpressions = (
	impressions: Impression[],
	reduxState: State,
) => {
	const dedupeByType = new Map<ImpressionType, Set<string>>();

	return impressions.reduce<Record<ImpressionType, SegmentImpression[]>>(
		(acc, currentValue) => {
			const type = currentValue.list;
			const dedupeKey = `${currentValue?.id ?? 'unknown'}:${currentValue?.position}`;

			if (!dedupeByType.has(type)) {
				dedupeByType.set(type, new Set());
			}

			const seenKeys = dedupeByType.get(type);
			if (!seenKeys || seenKeys.has(dedupeKey)) {
				return acc;
			}

			seenKeys.add(dedupeKey);

			if (acc[type]) {
				acc[type].push(formatSegmentVendorImpression(currentValue, reduxState));
			} else {
				acc[type] = [formatSegmentVendorImpression(currentValue, reduxState)];
			}

			return acc;
		},
		{} as Record<ImpressionType, SegmentImpression[]>,
	);
};

export const getAppliedFiltersForBucketedImpressions = (
	state: State,
	appliedFilters?: Record<string, string[]> | null,
) => {
	if (state.page.pageType !== 'city') return null;
	return appliedFilters || null;
};

export const determineVendorTier = (
	vendor: Raw | Similar,
	marketCode: string,
) => {
	const marketSalesProfile = vendor.salesProfiles?.find(
		(profile) => profile.marketCode === marketCode,
	);

	return marketSalesProfile?.vendorTier ?? vendor.vendorTier;
};
